"use client";

import { m, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import FadeIn from "../ui/FadeIn";
import DotGlobe from "../ui/DotGlobe";
import { useTrialForm } from "@/components/providers/TrialFormProvider";

export default function CTA() {
  const { openTrialForm } = useTrialForm();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1, 0.95]);

  return (
    <section id="cta" ref={ref} className="py-16 sm:py-24 lg:py-32 short:py-8 shorter:py-5 px-5 sm:px-6 lg:px-8">
      <m.div
        style={{ scale }}
        className="max-w-[1400px] mx-auto"
      >
        <div className="relative rounded-xl overflow-hidden bg-[#0a0a0a] p-8 sm:p-12 md:p-20 lg:p-28 min-h-[400px] sm:min-h-[500px] flex items-center">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:60px_60px]" aria-hidden="true" />

          <DotGlobe />

          <div className="relative z-10 max-w-3xl">
            <FadeIn>
              <p className="text-xs font-medium tracking-[0.3em] uppercase text-white/70 mb-8">
                Get started today
              </p>
            </FadeIn>

            <FadeIn delay={0.1}>
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-[family-name:var(--font-display)] tracking-[-0.03em] text-white leading-[1]">
                The only AI with a
                <br />
                <span className="text-white/70">no hallucination guarantee</span>
              </h2>
            </FadeIn>

            <FadeIn delay={0.3}>
              <p className="mt-6 text-base text-white/70 font-light leading-relaxed max-w-lg">
                Join hundreds of institutional investors who trust Hudson Labs
                for research and risk assessment.
              </p>
            </FadeIn>

            <FadeIn delay={0.5}>
              <div className="flex flex-col sm:flex-row items-start gap-4 mt-10">
                <m.button
                  onClick={openTrialForm}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="group relative inline-flex items-center gap-3 px-8 py-4 rounded-md overflow-hidden"
                >
                  <span className="absolute inset-0 bg-white" />
                  <span className="absolute inset-0 bg-gradient-to-r from-neutral-800 to-black translate-x-full group-hover:translate-x-0 transition-transform duration-700 ease-[cubic-bezier(0.76,0,0.24,1)]" />
                  <span className="relative text-sm font-medium text-black group-hover:text-white transition-colors duration-700">
                    Start Free Trial
                  </span>
                  <span className="relative w-6 h-6 rounded-full bg-black/10 group-hover:bg-white/20 flex items-center justify-center transition-colors duration-700">
                    <svg className="w-3.5 h-3.5 text-black group-hover:text-white transition-colors duration-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </m.button>
              </div>
            </FadeIn>

            <FadeIn delay={0.7}>
              <div className="flex items-center gap-6 mt-12">
                <div className="flex -space-x-2">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full border-2 border-[#0a0a0a] bg-gradient-to-br from-white/10 to-white/20 flex items-center justify-center"
                    >
                      <span className="text-[9px] font-bold text-white/60">
                        {["JD", "KT", "AB", "ML"][i]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </m.div>
    </section>
  );
}
